import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRANSCRIPTION_LANGUAGES } from "@/lib/indianLanguages";
import { beginRecording, MAX_AUDIO_BYTES, releaseVoiceInput } from "./voiceRecording";

const EMPTY = { visible: false, phase: "idle", seconds: 0, error: "", clipUrl: "", transcript: "" };

export const useVoiceInput = (active, ensureSession) => {
  const { user, loading } = useAuth();
  const [state, setState] = useState(EMPTY), [language, setLanguage] = useState("auto");
  const runtime = useRef({ generation: 0, controller: null, stream: null, recorder: null, file: null, url: null });
  const update = useCallback((values) => setState((old) => ({ ...old, ...values })), []);
  const cancel = useCallback(() => { releaseVoiceInput(runtime.current); setState(EMPTY); }, []);
  useEffect(() => {
    const current = runtime.current; cancel();
    const hidden = () => { if (document.hidden) cancel(); };
    document.addEventListener("visibilitychange", hidden);
    return () => { document.removeEventListener("visibilitychange", hidden); releaseVoiceInput(current); };
  }, [active, user?.user_id, loading, cancel]);
  const acceptClip = useCallback((file) => {
    if (file.size > MAX_AUDIO_BYTES || !/\.(mp3|mp4|mpeg|mpga|m4a|wav|webm)$/i.test(file.name)) {
      update({ phase: "idle", error: "Use a supported audio file, up to 5 MB and 60 seconds." }); return;
    }
    const current = runtime.current;
    if (current.url) URL.revokeObjectURL(current.url);
    current.file = file; current.url = URL.createObjectURL(file);
    update({ phase: "review", error: "", clipUrl: current.url, transcript: "" });
  }, [update]);
  const start = () => {
    releaseVoiceInput(runtime.current);
    update({ visible: true, clipUrl: "", transcript: "" });
    beginRecording(runtime.current, update, acceptClip);
  };
  const stopRecording = () => {
    const recorder = runtime.current.recorder;
    if (recorder?.state === "recording") { recorder.stop(); update({ phase: "stopping" }); }
  };
  const transcribe = async () => {
    const current = runtime.current, generation = current.generation;
    if (!current.file || current.controller) return;
    const controller = new AbortController(); current.controller = controller;
    update({ phase: "transcribing", error: "" });
    try {
      const session = await ensureSession();
      if (generation !== current.generation) return;
      if (!session) throw new Error("Please wait for your conversation to load and try again.");
      const form = new FormData();
      form.append("session_id", session); form.append("model", "whisper-1"); form.append("language", language);
      form.append("audio", current.file, current.file.name);
      const { data } = await axios.post(`${API}/ai/transcribe`, form, { signal: controller.signal, timeout: 85000 });
      if (generation !== current.generation) return;
      update({ phase: "done", transcript: data.text, error: "" });
    } catch (error) {
      if (generation !== current.generation || axios.isCancel(error)) return;
      const detail = error.response?.data?.detail;
      update({ phase: "review", error: typeof detail === "string" ? detail : "Transcription could not finish. Please retry or type your question." });
    } finally { if (generation === current.generation) current.controller = null; }
  };
  return { ...state, language, loading, ownerKey: loading ? "loading" : user?.user_id || "guest", busy: ["requesting", "recording", "stopping", "transcribing"].includes(state.phase),
    open: () => update({ visible: true }), cancel, start, stopRecording, transcribe, acceptClip,
    setLanguage: (value) => { if (TRANSCRIPTION_LANGUAGES.some((item) => item.id === value)) setLanguage(value); },
    editTranscript: (transcript) => update({ transcript }) };
};